import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Input, Pagination } from "antd";
import { NOTIFICATION_TYPES } from "../../constants/commonConstants";
import Table from "../../components/Common/Table";
import TableHeader from "../../components/Common/TableHeader";
import DetailPopover from "../../components/Common/DetailPopover";
import NotFound from "../../components/NotFound";
import Loader from "../../components/Common/Loader";
import { formatDD_MM_YYYY_HH_MM } from "../../utils/dateFormating";

import { useThunk } from "../../hooks/useThunk";
import { useSelector } from "react-redux";
import { fetchNotifications, sendNotification, toggleAutoRemainder, getUserProfile } from "../../store";
import showToast from "../../utils/toastNotifications";

const { Search } = Input;

const NotificationCenter = () => {

  const [doGetUserProfile, isFetchingUserProfile, fetchUserProfileError] = useThunk(getUserProfile);
  const [doFetchNotifications, isFetchNotifications, fetchNotificationsError] = useThunk(fetchNotifications);
  const [doSendNotification, isSendingNotification, sendNotificationError] = useThunk(sendNotification);
  const [doToggleAutoRemainder, isTogglingAutoRemainder, toggleAutoRemainderError] = useThunk(toggleAutoRemainder);

  const [notifications, setNotifications] = useState([]);
  const [notificationAutoRemainder, setNotificationAutoRemainder] = useState(true);
  const [pagination, setPagination] = useState({});
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page")) || 1;
  const searchTerm = searchParams.get("search") || "";
  const [search, setSearch] = useState(searchTerm);
  

  const itemsPerPage = 10;

  useEffect(() => {
    const fetchUserProfile = async () => {
      const result = await doGetUserProfile();
      if (result?.success) {
        setNotificationAutoRemainder(result?.response?.data?.userData.onAutoRemainders);
        
      } else {
        
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    fetchData();
  }, [searchParams]);

  const fetchData = async (page = currentPage, keyword = search) => {
    const query = {
      page,
      limit: itemsPerPage,
      ...(keyword && { search: keyword }),
    };

    const notifications = await doFetchNotifications(query);
    const data = notifications?.response?.data;
    const paginations = notifications?.response?.pagination || {};

    

    if (notifications?.success) {
      setNotifications(data);
      setIsEmptyData(data?.length === 0 || !data);
      setPagination(paginations);
    }
  };

  const handleSendNotification = async (tenantID) => {
    

    const response = await doSendNotification({ tenantID });
    if (response?.success) {
      showToast("success", "Notification sent successfully!");
      fetchData(); // Refresh notifications after sending
    } else {
      showToast("error", response?.message || "Failed to send notification.");
    }
  };

  const handlePageChange = (page) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", page);
      if (search) newParams.set("search", search);
      return newParams;
    });
  };

  const handleSearch = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("search", search);
      newParams.set("page", 1);
      return newParams;
    });
  };

  const columns = [
    {
      title: "Date Sent",
      dataIndex: "dateSent",
      key: "dateSent",
      render: (text) => <span>{formatDD_MM_YYYY_HH_MM(text)}</span>,
    },
    {
      title: "Tenant Name",
      dataIndex: "tenantName",
      key: "tenantName",
    },
    {
      title: "Unit ID / Name",
      dataIndex: "unitId",
      key: "unitId",
    },
    {
      title: "Notification Method",
      dataIndex: "notificationMethod",
      key: "notificationMethod",
      render: (text) => (
        <span>
          {text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()}
        </span>
      ),
    },
    {
      title: "Notification Type",
      dataIndex: "notificationType",
      key: "notificationType",
      render: (text) => (
        <span
          className={`${
            text === NOTIFICATION_TYPES.SCHEDULED
              ? "text-[#C46500]"
              : "text-[#07C01D]"
          } font-[400] leading-6 text-[12px] font-inter capitalize tracking-[0%]`}
        >
          {text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()}
        </span>
      ),
    },
    {
      title: "Content Preview",
      dataIndex: "contentPreview",
      key: "contentPreview",
      render: (text) => {
        if (!text)
          return (
            <DetailPopover placement="top" text="-">
              -
            </DetailPopover>
          );

        return (
          <DetailPopover placement="top" text={text}>
            {text.length > 30 ? `${text.slice(0, 16)}...` : text}
          </DetailPopover>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      render: (_, record) => (
        <Button
          type="default"
          className="border-[#4772F1] text-[#4772F1] leading-6 rounded-[15px] h-[28px] px-3 py-[2px] text-[9px] sm:text-[10px] md:text-[11px] lg:text-[12px] font-[400]"
          onClick={() => handleSendNotification(record.tenantId)}
        >
          Send Notification
        </Button>
      ),
    },
  ];

  const handleAutoRemainderChange = async () => {
    setNotificationAutoRemainder((prev) => !prev);
    const response = await doToggleAutoRemainder();
    if (response?.success) {
      
    } else {
      setNotificationAutoRemainder((prev) => !prev); 
      
    }
  };
  return (
    <div className="p-4 sm:p-6 md:p-[30px]">
      <div className="">
        {/* Table Header */}
        <div className="mb-[30px]">
          <TableHeader
            searchValue={search}
            onSearchChange={setSearch}
            onSearchSubmit={handleSearch}
            searchPlaceholder="Search by tenant name or unit ID"
            buttons={[
              {
                label: "SEARCH",
                onClick: handleSearch,
                variant: "main",
              },
            ]}
            isAutoRemainder={true}
            isUserLoading={isFetchingUserProfile}
            isAutoRemainderDisabled={isTogglingAutoRemainder}
            isAutoRemainderEnabled={notificationAutoRemainder}
            onAutoRemainderChange={handleAutoRemainderChange}
            searchWrapperClass="w-full [@media(min-width:585px)]:w-auto"
            containerClass="md:grid md:grid-cols-3 flex flex-wrap sm:flex-nowrap"
            buttonWrapperClass="w-auto"
          />
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {isFetchNotifications ? (
            <div
              className="font-inter text-[15px] text-[rgb(112,112,112)] flex flex-col gap-[10px] items-center justify-center py-9 mt-3 w-full bg-white rounded-[10px]"
              style={{ boxShadow: "0px 4px 4px 0px #0000000F" }}
            >
              <div className="relative top-0 mt-40 left-0 right-0 bottom-0 flex justify-center items-center">
                <Loader color="#4772F1" />
              </div>
            </div>
          ) : fetchNotificationsError ? (
            <NotFound
              text1="Error fetching notifications"
              text2={
                fetchNotificationsError.message ||
                "An error occurred while fetching notifications."
              }
              isButton={false}
            />
          ) : isEmptyData ? (
            searchTerm?.trim() ? (
              <NotFound
                text1="No notifications found"
                text2="No notifications match your current filter criteria."
                isButton={false}
              />
            ) : (
              <NotFound
                text1="No notifications have been sent yet."
                isButton={false}
              />
            )
          ) : (
            <div className="">
              <Table
                dataSource={notifications}
                columns={columns}
                hasSubTable={false}
                rowKey="_id"
              />

              {/* Pagination */}
              <div className="flex justify-center">
                <Pagination
                  current={pagination?.currentPage || 1}
                  total={pagination?.totalRecords || 0}
                  pageSize={itemsPerPage}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  className="custom-pagination"
                  hideOnSinglePage={true}
                  showLessItems
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
